import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Home, X, RotateCcw } from 'lucide-react';
import AnswerCard from './AnswerCard';
import QuestionRenderer from './QuestionRenderer';
import QuestionHtmlWithZoom from '../../../components/QuestionHtmlWithZoom';
import { getImageUrl } from '../../../utils/questionHtml';
import { categoryStore } from '../../../store/categoryStore';
import { typeNames, subTypeNames } from '../../../constants/questionTypes';
import type { Question, Paper } from '../types';

interface PracticeModeViewProps {
  questions: Question[];
  paper: Paper;
  currentUser: any;
  practiceAnswers: Record<string, string>;
  revealedQuestions: Set<string>;
  currentIndex: number;
  showSidebar: boolean;
  onExitPracticeMode: () => void;
  onBack?: () => void;
  isWrongQuestionMode?: boolean;
  onAnswerChange: (questionId: string, answer: string) => void;
  onTextAnswerChange: (questionId: string, text: string) => void;
  onSetCurrentIndex: (index: number) => void;
  onSetShowSidebar: (show: boolean) => void;
  onSetRevealedQuestions: (updater: (prev: Set<string>) => Set<string>) => void;
  theme: 'blue' | 'rose';
  titleFallback: string;
}

// 主题样式映射
const themeStyles = {
  blue: {
    bg: 'bg-gray-50',
    headerGradient: 'from-blue-50 to-blue-100',
    headerBorder: 'border-blue-200',
    typeBadge: 'bg-blue-600',
    navButton: 'bg-blue-600 hover:bg-blue-700',
    countBadge: 'bg-blue-50 border-blue-200 text-blue-700',
    numberColor: 'text-blue-600',
    mobileButton: 'bg-blue-600 hover:bg-blue-700',
  },
  rose: {
    bg: 'bg-gradient-to-br from-rose-50 via-white to-pink-50',
    headerGradient: 'from-rose-50 to-pink-100',
    headerBorder: 'border-rose-200',
    typeBadge: 'bg-rose-600',
    navButton: 'bg-rose-600 hover:bg-rose-700',
    countBadge: 'bg-rose-50 border-rose-200 text-rose-700',
    numberColor: 'text-rose-600',
    mobileButton: 'bg-rose-600 hover:bg-rose-700',
  },
};

export default function PracticeModeView({
  questions,
  paper,
  currentUser,
  practiceAnswers,
  revealedQuestions,
  currentIndex,
  showSidebar,
  onExitPracticeMode,
  onBack,
  isWrongQuestionMode = false,
  onAnswerChange,
  onTextAnswerChange,
  onSetCurrentIndex,
  onSetShowSidebar,
  onSetRevealedQuestions,
  theme,
  titleFallback,
}: PracticeModeViewProps) {
  const navigate = useNavigate();
  const styles = themeStyles[theme];
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(practiceAnswers).filter(key => !key.includes('_')).length;

  const getQuestionTypeName = (type: string, subType?: string) => {
    if (type === 'scratch_coding') return categoryStore.getCategoryNameByCode('scratch') || '图形化编程';
    if (type === 'coding' && subType) return subTypeNames[subType] || '主观题';
    return typeNames[type] || '单选题';
  };

  // 标题处理
  const displayTitle = paper?.title?.split(' - 收藏题目')[0]
    || paper?.title?.split(' - 错题回顾')[0]
    || paper?.title?.split('图形化')[0]
    || titleFallback;
  const hasGraphicSubtitle = paper?.title?.includes('图形化');
  const graphicSubtitle = hasGraphicSubtitle ? `图形化${paper.title.split('图形化')[1]}` : null;

  return (
    <div className={`min-h-screen ${styles.bg}`}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 lg:h-24 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            {onBack && (
              <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
                <ChevronLeft className="w-4 h-4" />
                <span className="text-xs lg:text-sm">返回</span>
              </button>
            )}
          </div>
          <div className="flex items-center min-w-0 px-2 gap-[1cm]">
            {/* 页面类型标签 - 柔和圆润风格 */}
            <span className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
              isWrongQuestionMode || theme === 'rose'
                ? 'bg-rose-50 text-rose-700 border border-rose-200 shadow-md shadow-rose-500/10 hover:shadow-rose-500/20'
                : 'bg-blue-50 text-blue-700 border border-blue-200 shadow-md shadow-blue-500/10 hover:shadow-blue-500/20'
            }`}>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                {isWrongQuestionMode || theme === 'rose' ? (
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                ) : (
                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd"/>
                )}
              </svg>
              {isWrongQuestionMode || theme === 'rose' ? '我的错题' : '我的收藏'}
            </span>
            <h1 className="font-bold text-gray-900 text-sm sm:text-base lg:text-2xl truncate max-w-[200px] sm:max-w-[400px] lg:max-w-none">
              {displayTitle}
            </h1>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <button
              onClick={onExitPracticeMode}
              className="px-5 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded font-medium transition-colors flex items-center gap-2 flex-shrink-0 shadow-sm hover:from-blue-600 hover:to-indigo-700"
            >
              <RotateCcw className="w-4 h-4" />
              回到复习模式
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-6">
        {/* Mobile Answer Card Button */}
        <button onClick={() => onSetShowSidebar(true)} className={`lg:hidden fixed bottom-4 right-4 z-40 w-12 h-12 ${styles.mobileButton} text-white rounded-full shadow-lg transition-colors flex items-center justify-center`}>
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Sidebar - Answer Card */}
          <div className="hidden lg:block lg:sticky lg:top-20 lg:self-start">
            <AnswerCard
              examQuestions={questions}
              currentIndex={currentIndex}
              answers={practiceAnswers}
              markedQuestions={new Set()}
              isSubmitted={false}
              paper={paper}
              revealedQuestions={revealedQuestions}
              isPracticeMode={true}
              onQuestionSelect={onSetCurrentIndex}
            />
          </div>

          {/* Question Area */}
          <div className="lg:col-span-3 space-y-3 sm:space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Question Header */}
              <div className={`sticky lg:top-20 z-30 bg-gradient-to-r ${styles.headerGradient} p-3 sm:p-4 lg:p-6 border-b ${styles.headerBorder}`}>
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                    <span className={`px-2 py-0.5 lg:px-3 lg:py-1 ${styles.typeBadge} text-white text-xs lg:text-sm font-medium rounded-full`}>
                      {getQuestionTypeName(currentQuestion?.type, currentQuestion?.subType)}
                    </span>
                    {revealedQuestions.has(currentQuestion?.id) && (
                      <span className={`px-2 py-0.5 lg:px-3 lg:py-1 text-xs lg:text-sm font-medium rounded-full ${
                        (() => {
                          const userAns = practiceAnswers[currentQuestion?.id];
                          if (!userAns) return 'bg-red-100 text-red-700';
                          const correctIds = currentQuestion?.options?.filter(o => o.isCorrect).map(o => o.id) || [];
                          if (currentQuestion?.type === 'multi_choice') {
                            const userIds = userAns.split(',');
                            const isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
                            return isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
                          }
                          const selectedOption = currentQuestion?.options?.find(o => o.id === userAns);
                          return selectedOption?.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
                        })()
                      }`}>
                        {(() => {
                          const userAns = practiceAnswers[currentQuestion?.id];
                          if (!userAns) return '未作答';
                          const correctIds = currentQuestion?.options?.filter(o => o.isCorrect).map(o => o.id) || [];
                          if (currentQuestion?.type === 'multi_choice') {
                            const userIds = userAns.split(',');
                            const isCorrect = userIds.length === correctIds.length && userIds.every(id => correctIds.includes(id));
                            return isCorrect ? '回答正确' : '回答错误';
                          }
                          const selectedOption = currentQuestion?.options?.find(o => o.id === userAns);
                          return selectedOption?.isCorrect ? '回答正确' : '回答错误';
                        })()}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:flex items-center gap-2">
                    <button onClick={() => onSetCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0} className={`px-3 lg:px-4 py-1.5 lg:py-2 ${styles.navButton} text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all`}>
                      <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                    </button>
                    <button onClick={() => onSetCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))} disabled={currentIndex === questions.length - 1} className={`px-3 lg:px-4 py-1.5 lg:py-2 ${styles.navButton} text-white rounded-lg disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all`}>
                      下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                    </button>
                  </div>
                  {/* 编程题揭示按钮 */}
                  {!revealedQuestions.has(currentQuestion?.id) && currentQuestion?.type !== 'single_choice' && currentQuestion?.type !== 'multi_choice' && currentQuestion?.type !== 'judge' && currentQuestion?.type !== 'graphics' && (
                    <button
                      onClick={() => onSetRevealedQuestions(prev => new Set([...prev, currentQuestion?.id]))}
                      className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-xs lg:text-sm font-medium flex items-center gap-1.5"
                    >
                      查看答案
                    </button>
                  )}
                </div>
              </div>

              {/* Question Content */}
              <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
                <div className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <span className={`${styles.numberColor} font-bold flex-shrink-0`}>{currentIndex + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-3 relative">
                        {(() => {
                          const imagePosition = (currentQuestion as any).imagePosition || 'below';
                          const currentLayout = (currentQuestion as any)?.titleImageLayout || 'auto';
                          // 题干内容：优先使用 questionStem（题干富文本），回退到 content（兼容旧数据）
                          const questionStem = currentQuestion?.questionStem || currentQuestion?.content || '';
                          const hasTitleImages = currentQuestion?.titleImages && currentQuestion.titleImages.length > 0 && !questionStem?.includes('<img');
                          const isCodingQuestion = currentQuestion?.type === 'python_coding' ||
                                                   currentQuestion?.type === 'cpp_coding' ||
                                                   currentQuestion?.type === 'scratch_coding';
                          // 编程题题目名称：有 questionTitle 则按此内容显示，否则显示"未命名+序号"
                          const questionTitle = isCodingQuestion
                            ? (() => {
                                if (currentQuestion?.questionTitle) return currentQuestion.questionTitle;
                                const codingQuestions = questions.filter(q =>
                                  q.type === 'python_coding' || q.type === 'cpp_coding' || q.type === 'scratch_coding'
                                );
                                const codingIndex = codingQuestions.findIndex(q => q.id === currentQuestion?.id) + 1;
                                return `未命名${codingIndex}`;
                              })()
                            : null;

                          const titleText = questionStem?.includes('<img') || questionStem?.startsWith('<') ? (
                            <div className="prose prose-sm max-w-none">
                              <QuestionHtmlWithZoom
                                html={questionStem}
                                imageWidths={currentQuestion.titleImageWidths}
                              />
                            </div>
                          ) : (
                            <span>{questionStem}</span>
                          );

                          // 自动布局：根据图片数量决定单行还是多行
                          const imageCount = currentQuestion.titleImages?.length || 1;
                          const gridCols = imageCount <= 2 ? imageCount : 2;

                          const titleImagesEl = hasTitleImages ? (
                            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
                              {currentQuestion.titleImages?.map((imgUrl, idx) => {
                                const fullUrl = getImageUrl(imgUrl);
                                return (
                                  <div key={idx} className="relative overflow-hidden">
                                    <img
                                      src={fullUrl}
                                      alt={`题目图片 ${idx + 1}`}
                                      className="rounded-lg border border-gray-200"
                                      style={{ width: '100%', maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                      loading="lazy"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          ) : null;

                          return (
                            <>
                              {/* 编程题始终显示题目名称：有内容按内容显示，无内容显示"未命名+序号" */}
                              {isCodingQuestion && (
                                <div className="mb-3 pb-3 border-b border-gray-200">
                                  <span className="text-sm font-medium text-gray-500">题目名称：</span>
                                  <span className="text-base font-semibold text-gray-800">{questionTitle}</span>
                                </div>
                              )}
                              {imagePosition === 'above' && titleImagesEl}
                              {titleText}
                              {imagePosition === 'below' && titleImagesEl}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Answer Area */}
              <div className="p-3 sm:p-4 lg:p-6">
                <QuestionRenderer
                  key={currentQuestion?.id}
                  question={currentQuestion}
                  currentIndex={currentIndex}
                  answers={practiceAnswers}
                  isSubmitted={false}
                  isPreview={false}
                  paperId={paper.id}
                  paper={paper}
                  currentUser={currentUser}
                  aiExplanations={{}}
                  examQuestions={questions}
                  aiGradingEnabled={true}
                  isAssignmentMode={false}
                  revealedQuestions={revealedQuestions}
                  isPracticeMode={true}
                  onAnswerChange={onAnswerChange}
                  onTextAnswerChange={onTextAnswerChange}
                  onAIGrading={() => {}}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => onSetShowSidebar(false)}
            />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.3 }} className="fixed inset-y-0 right-0 w-[85vw] sm:w-80 bg-white shadow-xl z-50 lg:hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-bold text-gray-900">答题卡</h2>
                <button onClick={() => onSetShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-600" /></button>
              </div>
              <div className="p-4 overflow-y-auto">
                <AnswerCard
                  examQuestions={questions}
                  currentIndex={currentIndex}
                  answers={practiceAnswers}
                  markedQuestions={new Set()}
                  isSubmitted={false}
                  paper={paper}
                  revealedQuestions={revealedQuestions}
                  isPracticeMode={true}
                  onQuestionSelect={onSetCurrentIndex}
                  onCloseSidebar={() => onSetShowSidebar(false)}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}